import React, { useState, useEffect } from 'react';
import { getStaffAvails, StaffAvailsFilters, StaffAvail } from '../services/staffAvailsApi';
import { createStaffBooking, getStaffBookings, StaffBooking } from '../services/staffBookingsApi';
import './StaffAvailsList.css';

interface AvailsByDate {
  [date: string]: StaffAvail[];
}

const StaffAvailsList: React.FC = () => {
  const [avails, setAvails] = useState<StaffAvail[]>([]);
  const [bookings, setBookings] = useState<StaffBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [bookingInProgress, setBookingInProgress] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedAvail, setSelectedAvail] = useState<{avail: StaffAvail, date: string} | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [startHour, setStartHour] = useState<string>('');
  const [endHour, setEndHour] = useState<string>('');

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadAvails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvails = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: StaffAvailsFilters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      // Load both avails and bookings
      const [availsData, bookingsData] = await Promise.all([
        getStaffAvails(filters),
        getStaffBookings(filters)
      ]);

      setAvails(availsData.avails || []);
      setBookings(bookingsData.bookings || []);
    } catch (err: any) {
      console.error('Error loading staff avails:', err);
      setError(err.message || 'Failed to load staff avails');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadAvails();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(() => loadAvails(), 0);
  };

  // Helper function to check if a staff member is already booked for a date
  const getBookingForStaffMember = (date: string, staffEmail: string): StaffBooking | undefined => {
    return bookings.find(
      b => b.date === date && b.staff_email.toLowerCase() === staffEmail.toLowerCase()
    );
  };

  const handleBookStaffClick = (avail: StaffAvail, date: string) => {
    setSelectedAvail({ avail, date });
    setSelectedRoles([]);
    setStartHour('');
    setEndHour('');
    setShowRoleModal(true);
  };

  const handleConfirmBooking = async () => {
    if (!selectedAvail) return;

    const { avail, date } = selectedAvail;
    const bookingKey = `${date}-${avail.staff_email}`;
    setBookingInProgress(bookingKey);
    setError(null);
    setSuccessMessage(null);
    setShowRoleModal(false);

    try {
      await createStaffBooking({
        staff_email: avail.staff_email,
        staff_name: avail.staff_name,
        date: date,
        date_display: formatDate(date),
        roles: selectedRoles,
        start_hour: startHour || undefined,
        end_hour: endHour || undefined,
        send_email: true,  // Always send confirmation email when booking from avails
      });

      setSuccessMessage(`Successfully booked ${avail.staff_name} for ${formatDate(date)}`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Reload bookings to update the UI
      const filters: StaffAvailsFilters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      const bookingsData = await getStaffBookings(filters);
      setBookings(bookingsData.bookings || []);
    } catch (err: any) {
      console.error('Error booking staff:', err);
      const errorMsg = err.message || 'Failed to book staff';
      setError(errorMsg);
    } finally {
      setBookingInProgress(null);
      setSelectedAvail(null);
      setSelectedRoles([]);
      setStartHour('');
      setEndHour('');
    }
  };

  // Group avails by date
  const groupAvailsByDate = (): AvailsByDate => {
    const grouped: AvailsByDate = {};
    avails.forEach((avail) => {
      if (!grouped[avail.date]) {
        grouped[avail.date] = [];
      }
      grouped[avail.date].push(avail);
    });
    return grouped;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const availsByDate = groupAvailsByDate();
  const sortedDates = Object.keys(availsByDate).sort();

  return (
    <div className="staff-avails-list">
      <div className="staff-avails-header">
        <h2>Staff Availability</h2>
        <button onClick={loadAvails} className="refresh-button" disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="close-success">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-panel">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="start-date">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="apply-button">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="clear-button">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat">
          <span className="stat-label">Total Dates:</span>
          <span className="stat-value">{sortedDates.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Avails:</span>
          <span className="stat-value">{avails.length}</span>
        </div>
      </div>

      {/* Avails Display */}
      {loading ? (
        <div className="loading">Loading staff avails...</div>
      ) : sortedDates.length === 0 ? (
        <div className="no-data">No staff availabilities found.</div>
      ) : (
        <div className="dates-container">
          {sortedDates.map((date) => {
            const dateAvails = availsByDate[date];
            return (
              <div key={date} className="date-card">
                <div className="date-header">
                  <h3>{formatDate(date)}</h3>
                  <span className="date-count">{dateAvails.length} staff available</span>
                </div>
                <div className="staff-grid">
                  {dateAvails.map((avail, idx) => {
                    const bookingKey = `${date}-${avail.staff_email}`;
                    const isBooking = bookingInProgress === bookingKey;
                    const existingBooking = getBookingForStaffMember(date, avail.staff_email);

                    return (
                      <div key={`${avail.staff_email}-${idx}`} className="staff-avail-card">
                        <div className="staff-name">{avail.staff_name}</div>
                        <div className="staff-email">{avail.staff_email}</div>
                        <div className="staff-actions">
                          {existingBooking ? (
                            <span className="booked-badge">✓ Booked</span>
                          ) : (
                            <button
                              onClick={() => handleBookStaffClick(avail, date)}
                              disabled={isBooking}
                              className="book-button"
                              title="Book staff member"
                            >
                              {isBooking ? '...' : 'Book'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Role Selection Modal */}
      {showRoleModal && selectedAvail && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select Roles for {selectedAvail.avail.staff_name}</h3>
            <p className="modal-subtitle">Date: {formatDate(selectedAvail.date)}</p>

            <div className="roles-selection">
              {['Setup', 'Seating', 'Check-in', 'Teardown', 'Camera'].map(role => (
                <label key={role} className="role-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRoles([...selectedRoles, role]);
                      } else {
                        setSelectedRoles(selectedRoles.filter(r => r !== role));
                      }
                    }}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>

            <div className="schedule-section">
              <h4>Schedule (Optional)</h4>
              <div className="schedule-inputs">
                <div className="time-input-group">
                  <label htmlFor="modal-start-time">Start Time</label>
                  <input
                    type="time"
                    id="modal-start-time"
                    value={startHour}
                    onChange={(e) => setStartHour(e.target.value)}
                  />
                </div>
                <div className="time-input-group">
                  <label htmlFor="modal-end-time">End Time</label>
                  <input
                    type="time"
                    id="modal-end-time"
                    value={endHour}
                    onChange={(e) => setEndHour(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleConfirmBooking}
                className="confirm-booking-button"
                disabled={bookingInProgress !== null}
              >
                {bookingInProgress ? 'Booking...' : 'Confirm Booking'}
              </button>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedAvail(null);
                  setSelectedRoles([]);
                  setStartHour('');
                  setEndHour('');
                }}
                className="cancel-booking-button"
                disabled={bookingInProgress !== null}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffAvailsList;
